import { z } from 'zod';

/**
 * F40 — Promotions & Discount Codes frozen API contracts.
 *
 * A promotion is a per-venue discount the venue owner authors (subject to the
 * F38 `promotions` feature gate — Pro+ tier). It is one of three display modes:
 *   - `public`  — code-bearing, listed publicly (the F40 marketplace surface),
 *   - `private` — code-bearing, unlisted (shareable link only),
 *   - `auto`    — no code; auto-applies to every eligible booking at quote time.
 *
 * The discount is `percent` (basis points 1..10000 — i.e. 0.01%..100%) or
 * `flat` (USD minor units). Scope narrows applicability by sport / sub-venue /
 * day-of-week / time-of-day window. Caps bound total + per-user redemptions.
 *
 * Status is a 6-value lifecycle (`draft → scheduled → active → paused →
 * expired → cancelled`); admin suspension is an ORTHOGONAL `suspendedAt` flag,
 * NOT a status value (mirrors F32 `open_games.suspended_at`).
 *
 * Reservation flow: `PromotionEventConsumer.reserve` runs INSIDE `createBooking`'s
 * transaction (before the booking INSERT) to compute + lock the discount;
 * `recordRedemption` runs after, in the same tx, to write the redemption ledger
 * row. `PromotionLifecycleConsumer.onBookingCancelled` is the <6h reversal hook,
 * wired via `setF40Dependencies` on the NotificationOrchestrator (mirrors F31/
 * F32/F34/F35 consumer-injection precedent). `PromoLookupForSponsored` is the
 * published-for-F39 read contract so featured placements can surface an active
 * sponsored promo on a venue card.
 *
 * All monetary fields are USD minor units (integer, cents). `discountValue` is
 * basis points for `percent`, minor units for `flat`.
 *
 * NOTE: `FEATURE_GATE_BLOCKED` is reused from F38's `SUBSCRIPTION_ERROR_CODES`
 * (the promotions feature gate) — it is NOT redeclared in this module.
 *
 * See plans/40_F40_promotions_and_discount_codes.md. Wave-2 sub-agents develop
 * against these contracts in parallel — DO NOT rename/drop/add fields without
 * escalating to the orchestrator first (contract-amendment protocol).
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/** Display mode (D — how/whether the promotion is listed + whether it carries a code). */
export const PROMOTION_DISPLAY_MODES = ['public', 'private', 'auto'] as const;
export type PromotionDisplayMode = (typeof PROMOTION_DISPLAY_MODES)[number];

/** Percent (basis points) vs flat (minor units) discount. */
export const PROMOTION_DISCOUNT_TYPES = ['percent', 'flat'] as const;
export type PromotionDiscountType = (typeof PROMOTION_DISCOUNT_TYPES)[number];

/**
 * Promotion lifecycle status. Admin suspension is an ORTHOGONAL `suspendedAt`
 * flag (NOT a status value) — a suspended promotion keeps its lifecycle status
 * while `suspendedAt` gates applicability (mirrors F32 `open_games`).
 */
export const PROMOTION_STATUSES = [
  'draft',
  'scheduled',
  'active',
  'paused',
  'expired',
  'cancelled',
] as const;
export type PromotionStatus = (typeof PROMOTION_STATUSES)[number];

/**
 * F40 error vocabulary. Service maps to HTTP:
 *   - `*_NOT_FOUND` / `CODE_NOT_FOUND` → 404
 *   - `PROMOTION_STALE` → 409 (If-Match mismatch)
 *   - everything else → 422
 *
 * `FEATURE_GATE_BLOCKED` is reused from F38's `SUBSCRIPTION_ERROR_CODES` — do
 * NOT redeclare it here.
 */
export const PROMOTION_ERROR_CODES = [
  'PROMOTION_NOT_FOUND',
  'PROMOTION_STALE',
  'PROMOTION_FIELD_LOCKED',
  'PROMOTION_INVALID_TRANSITION',
  'PROMOTION_QUOTA_REACHED',
  'PROMOTION_CODE_TAKEN',
  'PROMOTION_CAP_REACHED',
  'PER_USER_CAP_REACHED',
  'INELIGIBLE_SCOPE',
  'PROMOTION_NOT_ACTIVE',
  'CODE_NOT_FOUND',
  'VENUE_NOT_BOOKABLE',
] as const;
export type PromotionErrorCode = (typeof PROMOTION_ERROR_CODES)[number];

/**
 * Non-applicability reasons returned by `PromotionEvaluation.reason` when a
 * promotion does not apply to a quote. Distinct from `PROMOTION_ERROR_CODES`:
 * evaluation is a non-throwing read (returns `applicable: false` + a reason)
 * whereas the error codes are thrown at write paths.
 */
export const PROMOTION_EVALUATION_REASONS = [
  'CODE_NOT_FOUND',
  'PROMOTION_NOT_ACTIVE',
  'INELIGIBLE_SCOPE',
  'PROMOTION_EXPIRED',
  'PROMOTION_CAP_REACHED',
  'PER_USER_CAP_REACHED',
  'VENUE_NOT_BOOKABLE',
] as const;
export type PromotionEvaluationReason = (typeof PROMOTION_EVALUATION_REASONS)[number];

/** Sort options for the public promotions marketplace. */
export const PROMOTION_SORT_OPTIONS = ['ending_soon', 'biggest_discount', 'newest'] as const;
export type PromotionSort = (typeof PROMOTION_SORT_OPTIONS)[number];

/**
 * The cancellation reversal window (Wave-2 Track B). When a promo-bearing
 * booking is cancelled WITHIN this many hours of its creation, the redemption
 * is reversed (`reversed_at` stamped + the promotion's `total_uses_count`
 * decremented) so the slot is freed for re-use. A cancellation at-or-after the
 * window leaves the redemption counted (the discount was effectively consumed).
 * `PromotionLifecycleConsumer.onBookingCancelled` is the single enforcement
 * point. 6h matches the F40 plan's early-cancellation reversal window.
 */
export const PROMOTION_EARLY_REFUND_WINDOW_HOURS = 6;
export const PROMOTION_EARLY_REFUND_WINDOW_MS =
  PROMOTION_EARLY_REFUND_WINDOW_HOURS * 60 * 60 * 1000;

// ─── Zod input schemas ───────────────────────────────────────────────────────

/**
 * Promotion applicability scope. Every field is optional + nullable — an
 * all-null scope means "applies to any booking at this venue". `timeStart` /
 * `timeEnd` are `HH:MM` venue-local strings and must be both-present-or-both-
 * absent (a single bound is meaningless without the other).
 */
export const PromotionScopeSchema = z
  .object({
    sportNames: z.array(z.string()).nullable().optional(),
    subVenueIds: z.array(z.string().uuid()).nullable().optional(),
    daysOfWeek: z.array(z.number().int().min(0).max(6)).nullable().optional(),
    // 24-hour `HH:MM`, real clock only: `00:00`..`23:59`. The loose `\d{2}:\d{2}`
    // previously admitted impossible clocks (`25:99`); the tightened regex blocks
    // them before they reach the venue-local time-band comparison.
    timeStart: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
      .nullable()
      .optional(),
    timeEnd: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
      .nullable()
      .optional(),
  })
  .refine(
    (v) => {
      const hasStart = v.timeStart !== undefined && v.timeStart !== null;
      const hasEnd = v.timeEnd !== undefined && v.timeEnd !== null;
      return hasStart === hasEnd;
    },
    { message: 'timeStart and timeEnd must be both present or both absent' },
  )
  // When a band is present, the end must be strictly after the start. The runtime
  // `scopeMatches` predicate assumes a NON-overnight band (`timeStart <= localHHMM
  // < timeEnd`); an inverted/overnight band would silently never match. The
  // lexicographic compare is valid because both bounds are zero-padded 24-hour
  // strings (`HH:MM` compares == chronologically).
  .refine(
    (v) => {
      const hasStart = v.timeStart !== undefined && v.timeStart !== null;
      const hasEnd = v.timeEnd !== undefined && v.timeEnd !== null;
      if (!hasStart || !hasEnd) return true; // both-absent is handled by the prior refine.
      return v.timeEnd! > v.timeStart!;
    },
    {
      message: 'timeEnd must be after timeStart (overnight bands are not supported)',
      path: ['timeEnd'],
    },
  );
export type PromotionScope = z.infer<typeof PromotionScopeSchema>;

/**
 * The inner (un-refined) shape of the create payload. Kept as a standalone
 * base `z.object` so `UpdatePromotionInputSchema` can `.partial()` it — a
 * `ZodEffects` (the result of `.refine`) does not expose `.partial()`. The
 * create cross-field invariants live in the refined `CreatePromotionInputSchema`
 * below; the update schema is intentionally permissive (post-first-redemption
 * field-locking is enforced at the SERVICE layer, not here).
 */
export const PromotionInputBaseSchema = z.object({
  displayMode: z.enum(PROMOTION_DISPLAY_MODES),
  code: z.string().min(3).max(32).optional(),
  discountType: z.enum(PROMOTION_DISCOUNT_TYPES),
  discountValue: z.number().int().positive(),
  discountCurrency: z.string().length(3).default('USD'),
  scope: PromotionScopeSchema,
  validFrom: z.string().datetime({ offset: true }),
  validUntil: z.string().datetime({ offset: true }),
  totalUsesCap: z.number().int().positive().nullable().optional(),
  perUserUsesCap: z.number().int().positive().nullable().optional(),
});

/**
 * `POST /me/venues/:venueId/promotions` body. Cross-field invariants:
 *   1. `validUntil` must be strictly after `validFrom`.
 *   2. `code` MUST be present when `displayMode !== 'auto'` and MUST be
 *      absent/undefined when `displayMode === 'auto'`.
 *   3. `percent`: `discountValue` in 1..10000 (basis points). `flat`:
 *      `discountValue > 0` (minor units; `.positive()` already guarantees this).
 */
export const CreatePromotionInputSchema = PromotionInputBaseSchema.refine(
  (v) => new Date(v.validUntil).getTime() > new Date(v.validFrom).getTime(),
  { message: 'validUntil must be after validFrom', path: ['validUntil'] },
)
  .refine((v) => (v.displayMode === 'auto' ? v.code === undefined : v.code !== undefined), {
    message: 'code is required unless displayMode is "auto", and forbidden when it is',
    path: ['code'],
  })
  .refine(
    (v) =>
      v.discountType === 'percent'
        ? v.discountValue >= 1 && v.discountValue <= 10000
        : v.discountValue > 0,
    {
      message: 'percent discountValue must be 1..10000 basis points; flat must be positive',
      path: ['discountValue'],
    },
  );
export type CreatePromotionInput = z.infer<typeof CreatePromotionInputSchema>;

/**
 * `PATCH /me/venues/:venueId/promotions/:id` body. Derived from the permissive
 * base object via `.partial()` (post-first-redemption field-locking is enforced
 * at the SERVICE layer per the F12 precedent — the schema stays permissive so
 * legitimate edits to unlocked fields are not blocked by the schema).
 */
export const UpdatePromotionInputSchema = PromotionInputBaseSchema.partial();
export type UpdatePromotionInput = z.infer<typeof UpdatePromotionInputSchema>;

/**
 * `POST /me/promotions/evaluate` (and the internal quote-time check). Computes
 * whether a promotion (by `code`, or the active auto-promo when `code` omitted)
 * applies to a candidate booking, returning the discount breakdown.
 */
export const EvaluatePromotionInputSchema = z.object({
  venueId: z.string().uuid(),
  subVenueId: z.string().uuid(),
  sportName: z.string().min(1),
  startAt: z.string().datetime({ offset: true }),
  endAt: z.string().datetime({ offset: true }),
  // Same bounds as a created code and `CreateBookingInput.appliedPromotionCode`,
  // so a code that passes the advisory check can also be booked.
  code: z.string().min(3).max(32).optional(),
});
export type EvaluatePromotionInput = z.infer<typeof EvaluatePromotionInputSchema>;

/**
 * `POST /admin/promotions/:id/suspend` body. Orthogonal admin suspension
 * (sets `suspendedAt` + `suspensionReason`); does NOT change the lifecycle
 * `status`. Mandatory reason (audit-logged).
 */
export const SuspendPromotionInputSchema = z.object({
  reason: z.string().min(1).max(500),
});
export type SuspendPromotionInput = z.infer<typeof SuspendPromotionInputSchema>;

// ─── Read-side projections ───────────────────────────────────────────────────

/**
 * One `promotions` row. `code` is null for `auto` promotions. `totalUsesCount`
 * is the live redemption counter (drives the cap check). The orthogonal
 * `suspendedAt`/`suspendedByAdminId`/`suspensionReason` trio is null unless an
 * admin has suspended the promotion. `firstRedeemedAt` anchors the service-layer
 * field-locking (most economic fields lock after the first redemption).
 * All monetary fields are USD minor units; `discountValue` is basis points for
 * `percent`, minor units for `flat`.
 */
export type Promotion = {
  readonly id: string;
  readonly venueId: string;
  readonly ownerB2bAccountId: string;
  readonly code: string | null;
  readonly displayMode: PromotionDisplayMode;
  readonly discountType: PromotionDiscountType;
  readonly discountValue: number;
  readonly discountCurrency: string;
  readonly scope: PromotionScope;
  readonly validFrom: string; // ISO UTC
  readonly validUntil: string; // ISO UTC
  readonly totalUsesCap: number | null;
  readonly totalUsesCount: number;
  readonly perUserUsesCap: number | null;
  readonly status: PromotionStatus;
  readonly firstRedeemedAt: string | null;
  readonly suspendedAt: string | null;
  readonly suspendedByAdminId: string | null;
  readonly suspensionReason: string | null;
  readonly createdByUserId: string;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * One `promotion_redemptions` ledger row (append-only). Written by
 * `recordRedemption` inside `createBooking`'s tx. `reversedAt`/`reversalReason`
 * are set by the `onBookingCancelled` <6h reversal hook (the redemption counter
 * is decremented + the row is marked reversed, not deleted). All monetary
 * fields are USD minor units.
 */
export type PromotionRedemption = {
  readonly id: string;
  readonly promotionId: string;
  readonly bookingId: string;
  readonly userId: string;
  readonly venueId: string;
  readonly redeemedAt: string; // ISO UTC
  readonly grossPriceMinor: number;
  readonly discountAmountMinor: number;
  readonly netPriceMinor: number;
  readonly discountType: PromotionDiscountType;
  readonly discountValue: number;
  readonly reversedAt: string | null;
  readonly reversalReason: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
};

/**
 * Result of `POST /me/promotions/evaluate`. `applicable` is true iff a
 * promotion applies; `promotion` is a thin reference (null when not applicable);
 * `reason` carries the non-applicability cause (null when applicable).
 * `autoApplied` is true when the discount came from an `auto` promotion rather
 * than a redeemed code. All monetary fields are USD minor units.
 */
export type PromotionEvaluation = {
  readonly applicable: boolean;
  readonly promotion: {
    readonly id: string;
    readonly code: string | null;
    readonly displayMode: PromotionDisplayMode;
    readonly discountType: PromotionDiscountType;
    readonly discountValue: number;
  } | null;
  readonly discountAmount: number;
  readonly grossPrice: number;
  readonly netPrice: number;
  readonly autoApplied: boolean;
  readonly reason: PromotionEvaluationReason | null;
};

/**
 * Aggregated performance metrics for a single promotion (owner + admin
 * dashboards). All monetary fields are USD minor units.
 */
export type PromotionMetrics = {
  readonly totalRedemptions: number;
  readonly totalDiscountMinor: number;
  readonly totalNetMinor: number;
  readonly distinctUsers: number;
  readonly byDay: ReadonlyArray<{ readonly day: string; readonly count: number }>;
  readonly bySport: ReadonlyArray<{ readonly sportName: string; readonly count: number }>;
  readonly byCourt: ReadonlyArray<{ readonly subVenueId: string; readonly count: number }>;
};

/**
 * Thin reference to an active sponsored promotion, published for F39 featured
 * placements to surface on a venue card. `deepLinkPath` routes to the promotion
 * detail / redemption flow.
 */
export type SponsoredPromoRef = {
  readonly promotionId: string;
  readonly venueId: string;
  readonly code: string | null;
  readonly displayMode: PromotionDisplayMode;
  readonly discountType: PromotionDiscountType;
  readonly discountValue: number;
  readonly deepLinkPath: string;
  readonly validUntil: string;
};

// ─── Consumer / lookup interfaces ────────────────────────────────────────────

/**
 * Input to `PromotionEventConsumer.reserve`. Runs INSIDE `createBooking`'s
 * transaction (before the booking INSERT) to find + lock the applicable
 * discount for the candidate booking. `tx` is the active Drizzle transaction
 * handle (typed `unknown` at the type-package layer per the
 * `PaymentEventConsumer` convention — the API project narrows at the call site).
 */
export interface PromotionReserveInput {
  readonly tx: unknown;
  readonly venueId: string;
  readonly subVenueId: string;
  readonly sportName: string;
  readonly startAt: string;
  readonly venueTimezone: string;
  readonly userId: string;
  readonly grossMinor: number;
  readonly code?: string;
}

/**
 * F41 (Option A — §2.5) — input to `PromotionEventConsumer.reserveTournament`.
 * Runs INSIDE the tournament-registration transaction (before the registration
 * INSERT). A tournament-scoped promotion matches by `sportName` + the tournament
 * (NO sub-venue / day / time scope, unlike a venue booking). The lock + cap
 * re-check are identical to the venue `reserve` path. Returns null when no
 * promotion applies (auto-match no-op); an explicit-code miss THROWS.
 */
export interface PromotionReserveTournamentInput {
  readonly tx: unknown;
  readonly tournamentId: string;
  readonly sportName: string;
  readonly userId: string;
  readonly grossMinor: number;
  readonly code?: string;
}

/**
 * Result of a successful `reserve` — the computed discount the booking will
 * carry. All monetary fields are USD minor units. `discountValue` is basis
 * points for `percent`, minor units for `flat`.
 */
export interface PromotionReserveResult {
  readonly promotionId: string;
  readonly discountType: PromotionDiscountType;
  readonly discountValue: number;
  readonly discountAmountMinor: number;
  readonly netMinor: number;
}

/**
 * Input to `PromotionEventConsumer.recordRedemption`. Runs AFTER the booking
 * (or tournament-registration — F41 Option A) INSERT, in the SAME transaction,
 * to write the `promotion_redemptions` ledger row + increment the redemption
 * counter. `idempotencyKey` deduplicates retries (mirrors the F23 commission
 * idempotency precedent). `tx` is the active Drizzle transaction handle (typed
 * `unknown`; narrowed at the call site).
 *
 * F41 (Option A — §2.5): the redemption TARGET is polymorphic — EXACTLY ONE of
 * `bookingId` (the venue-booking path, unchanged) or `tournamentRegistrationId`
 * (the tournament path) is set. `venueId` is still required (the ledger's
 * `venue_id` column is NOT NULL — the tournament path passes the tournament id
 * as the column value, the table has no FK on it). The booking path is byte-for-
 * byte unchanged: existing callers keep passing `bookingId` + the real venue.
 */
export interface PromotionRecordRedemptionInput {
  readonly tx: unknown;
  readonly promotionId: string;
  /** The venue-booking target (mutually exclusive with `tournamentRegistrationId`). */
  readonly bookingId?: string;
  /** The tournament-registration target (F41 Option A; mutually exclusive with `bookingId`). */
  readonly tournamentRegistrationId?: string;
  readonly userId: string;
  readonly venueId: string;
  readonly grossMinor: number;
  readonly discountAmountMinor: number;
  readonly netMinor: number;
  readonly discountType: PromotionDiscountType;
  readonly discountValue: number;
  readonly idempotencyKey?: string | null;
  /** Optional explicit country code for the ledger row (the tournament path has no venue to read it from). */
  readonly countryCode?: string;
}

/**
 * The F40-frozen consumer contract that `createBooking` invokes for promotion
 * application. `reserve` runs inside the booking transaction before the INSERT
 * (returns null when no promotion applies); `recordRedemption` runs after the
 * INSERT in the same transaction. The booking-engine call site receives this
 * INTERFACE — it never `new`s the concrete F40 service. Implementations MUST be
 * idempotent on `recordRedemption` (via `idempotencyKey`).
 *
 * F41 (Option A — §2.5): `reserveTournament` is the tournament-registration
 * analog of `reserve` — additive, the booking path is untouched.
 */
export interface PromotionEventConsumer {
  reserve(input: PromotionReserveInput): Promise<PromotionReserveResult | null>;
  reserveTournament(input: PromotionReserveTournamentInput): Promise<PromotionReserveResult | null>;
  recordRedemption(input: PromotionRecordRedemptionInput): Promise<void>;
}

/**
 * The cancellation reversal hook, wired via `setF40Dependencies` on the
 * NotificationOrchestrator (mirrors the F31/F32/F34/F35 consumer-injection
 * precedent). The consumer re-reads the redemption by `bookingId` (NOT trusting
 * the event payload), marks it reversed, and decrements the promotion's
 * redemption counter. Idempotent — a re-fire on an already-reversed redemption
 * is a no-op.
 *
 * `unconditional` selects the D7 reversal policy (refined post-Wave-2 — flag for
 * founder review):
 *   - `false`/omitted — BOOKER-initiated cancellation: reverse ONLY within
 *     {@link PROMOTION_EARLY_REFUND_WINDOW_HOURS} of the booking's creation
 *     (anti-abuse — blocks book→cancel→rebook cap-gaming).
 *   - `true` — venue-cancel / decline / auto-expire / admin-force-cancel: the
 *     booker did NOT choose to cancel, so reverse UNCONDITIONALLY (never burn
 *     the cap or penalise the booker), regardless of the booking's age.
 */
export interface PromotionLifecycleConsumer {
  onBookingCancelled(args: { bookingId: string; unconditional?: boolean }): Promise<void>;
  /**
   * F41 (Option A — §2.5) — the tournament-registration analog. Reverses the
   * redemption keyed on `tournamentRegistrationId` (NOT trusting the event
   * payload), idempotent. `unconditional` selects the same D7 policy: a
   * before-deadline withdrawal / organizer-or-admin cancellation reverses
   * unconditionally; an after-deadline withdrawal leaves the redemption counted
   * (the seat stays counted too — §17.10). Unlike the booking path there is NO
   * age-window check (the deadline gate lives in the registration service).
   */
  onTournamentRegistrationReversed(args: {
    tournamentRegistrationId: string;
    unconditional?: boolean;
  }): Promise<void>;
}

/**
 * The published-for-F39 read contract. Featured placements call this to surface
 * an active sponsored promotion on a venue card; returns null when the venue has
 * no active sponsored promo. F40 ships the implementation; F39 consumes the
 * INTERFACE only (never `new`s the concrete service).
 */
export interface PromoLookupForSponsored {
  getActiveSponsoredPromo(venueId: string): Promise<SponsoredPromoRef | null>;
}
